import { Button } from "@health/ui/components/button";
import { Input } from "@health/ui/components/input";
import { type FormEvent, type ReactNode, useState } from "react";

const GATE_PASSWORD = import.meta.env.VITE_GATE_PASSWORD;
const STORAGE_KEY = "gate-unlocked";

// Simple hash for sessionStorage fingerprinting. Not cryptographic —
// just ensures a password rotation (new deploy) invalidates prior unlocks.
function fingerprint(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
  }
  return hash.toString(36);
}

function isUnlocked(): boolean {
  if (!GATE_PASSWORD) return true;
  return sessionStorage.getItem(STORAGE_KEY) === fingerprint(GATE_PASSWORD);
}

/**
 * Lightweight page gate for mobile staging deployments.
 * The password is visible in the client JS bundle — this is a casual deterrent,
 * not a security boundary. VPN is the real access control.
 */
export function PageGate({ children }: { children: ReactNode }) {
  const [unlocked, setUnlocked] = useState(isUnlocked);
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);

  if (!GATE_PASSWORD || unlocked) return <>{children}</>;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (value === GATE_PASSWORD) {
      sessionStorage.setItem(STORAGE_KEY, fingerprint(GATE_PASSWORD));
      setUnlocked(true);
    } else {
      setError(true);
      setValue("");
    }
  }

  return (
    <div className="flex min-h-svh items-center justify-center bg-background p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-xl border bg-card p-6 shadow-lg"
      >
        <h1 className="text-center text-lg font-medium text-foreground">Access Required</h1>

        <div className="space-y-2">
          <label htmlFor="gate-password" className="text-sm text-muted-foreground">
            Password
          </label>
          <Input
            id="gate-password"
            type="password"
            autoFocus
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError(false);
            }}
            aria-invalid={error || undefined}
            className="h-11"
          />
          <div aria-live="polite" className="min-h-5 text-sm text-destructive">
            {error && "Incorrect password"}
          </div>
        </div>

        <Button type="submit" className="h-11 w-full">
          Continue
        </Button>
      </form>
    </div>
  );
}
